/**
 * Los gráficos de Emails, con el mismo estilo que los de WhatsApp (recharts, una sola escala, leyenda en HTML y el
 * tooltip en tinta):
 * - «Por día»: dos vistas, porque los envíos son miles y las interacciones decenas; en una sola escala las
 *   interacciones serían una línea pegada al piso.
 * - «A qué hora interactúan»: aperturas, clics y respuestas por hora del día (hora del negocio), apiladas, con la
 *   franja de dos horas que más junta dicha arriba.
 */

import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { FilaDia, FilaHora } from "../types";
import { COLORES, ddmm } from "../../wa-oficial/logic/estadisticas";
import { ESTILO_TOOLTIP, Leyenda, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { mejorFranja, textoFranja } from "../logic/emails";

const COLOR = {
  enviados: COLORES.enviados,
  rebotes: COLORES.advertencia,
  aperturas: COLORES.leidos,
  clics: COLORES.entregados,
  respuestas: "#9333ea",
  bajas: COLORES.bajas,
} as const;

type Vista = "envios" | "interacciones";
const SERIES: Record<Vista, { clave: keyof FilaDia; label: string; color: string }[]> = {
  envios: [
    { clave: "enviados", label: "Enviados", color: COLOR.enviados },
    { clave: "rebotes", label: "Rebotes", color: COLOR.rebotes },
  ],
  interacciones: [
    { clave: "aperturas", label: "Aperturas", color: COLOR.aperturas },
    { clave: "clics", label: "Clics", color: COLOR.clics },
    { clave: "respuestas", label: "Respuestas", color: COLOR.respuestas },
    { clave: "bajas", label: "Bajas", color: COLOR.bajas },
  ],
};

export function GraficoDiario({ serie, alto = 220, sinAperturas = false }: { serie: FilaDia[]; alto?: number; sinAperturas?: boolean }) {
  const [vista, setVista] = useState<Vista>("envios");
  const hay = (serie || []).some((f) => f.enviados > 0 || f.aperturas > 0 || f.clics > 0 || f.respuestas > 0);
  if (!hay) return <Vacio>No salió ningún mail en estos días.</Vacio>;
  const datos = serie.map((f) => ({ ...f, etiqueta: ddmm(f.fecha) }));
  const series = SERIES[vista].filter((s) => !(sinAperturas && s.clave === "aperturas"));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Leyenda items={series.map((s) => ({ color: s.color, label: s.label, linea: true }))} />
        <div role="group" aria-label="Qué mostrar" className="inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
          {(["envios", "interacciones"] as const).map((v) => (
            <button key={v} type="button" aria-pressed={vista === v} onClick={() => setVista(v)}
              className={`h-7 px-2.5 rounded-md ${vista === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {v === "envios" ? "Envíos" : "Interacciones"}
            </button>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: alto }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datos} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid stroke={COLORES.grilla} vertical={false} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} width={48} />
            <Tooltip {...ESTILO_TOOLTIP} formatter={(v) => Number(v).toLocaleString("es-AR")} />
            {series.map((s) => (
              <Line key={String(s.clave)} type="monotone" dataKey={String(s.clave)} name={s.label} stroke={s.color} strokeWidth={2}
                dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {vista === "interacciones" && (
        <p className="text-[11px] text-slate-400">Cada apertura y cada clic cuentan, también los de una misma persona; las respuestas, por el día en que llegaron.</p>
      )}
    </div>
  );
}

const SERIES_HORA: { clave: keyof FilaHora; label: string; color: string }[] = [
  { clave: "aperturas", label: "Aperturas", color: COLOR.aperturas },
  { clave: "clics", label: "Clics", color: COLOR.clics },
  { clave: "respuestas", label: "Respuestas", color: COLOR.respuestas },
];

export function GraficoHoras({ porHora, sinAperturas = false, alto = 200 }: { porHora: FilaHora[]; sinAperturas?: boolean; alto?: number }) {
  const series = SERIES_HORA.filter((s) => !(sinAperturas && s.clave === "aperturas"));
  const filas = Array.from({ length: 24 }, (_x, h) => {
    const f = porHora?.find((x) => x.hora === h) || { hora: h, aperturas: 0, clics: 0, respuestas: 0 };
    return { ...f, aperturas: sinAperturas ? 0 : f.aperturas, etiqueta: `${h} h` };
  });
  const franja = mejorFranja(filas);
  if (!filas.some((f) => f.aperturas + f.clics + f.respuestas > 0)) return <Vacio>Todavía no hay aperturas, clics ni respuestas.</Vacio>;
  return (
    <div className="space-y-3">
      {franja ? (
        <p className="text-sm text-slate-700">
          La franja que más se mueve es la de <strong>{textoFranja(franja)}</strong>: junta el {Math.round(franja.pct * 100)} % de las interacciones.
        </p>
      ) : (
        <p className="text-sm text-slate-500">Con tan pocas interacciones todavía no se puede decir cuál es la mejor hora.</p>
      )}
      <Leyenda items={series.map((s) => ({ color: s.color, label: s.label }))} />
      <div style={{ width: "100%", height: alto }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={filas} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid stroke={COLORES.grilla} vertical={false} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 10, fill: COLORES.eje }} tickLine={false} axisLine={false} interval={2} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: COLORES.eje }} tickLine={false} axisLine={false} width={40} />
            <Tooltip {...ESTILO_TOOLTIP} formatter={(v) => Number(v).toLocaleString("es-AR")} />
            {series.map((s) => (
              <Bar key={String(s.clave)} dataKey={String(s.clave)} name={s.label} stackId="h" fill={s.color} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-slate-400">En la hora de Buenos Aires.</p>
    </div>
  );
}
